import { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Check, Copy } from "lucide-react";

interface CopyableCodeProps {
  /** The code to display + copy. Empty/null renders a dash. */
  code: string | null | undefined;
  /**
   * Optional aria-label override for the copy button. Default:
   * `Copy code ${code}`.
   */
  ariaLabel?: string;
  /**
   * Optional test id for the copy button. Useful for E2E targeting.
   */
  testId?: string;
}

/**
 * Renders an 8-char (or arbitrary length) code in monospace with a small
 * adjacent copy-to-clipboard button. Used in the admin Users and Attendees
 * tables for the reservation code column.
 *
 * Why a button rather than whole-cell click: the Users and Attendees rows
 * have an `onRowClick` that navigates to the user detail page. A whole-cell
 * click for copy would conflict — clicking the cell would also fire
 * row-navigation. The IconButton approach calls `e.stopPropagation()` on
 * click so the row navigation is suppressed.
 *
 * Visual feedback: on copy, the icon flips to a check for ~1.5s and the
 * tooltip flashes "Copied!". Empty/null code renders a dash and no button.
 */
export function CopyableCode({ code, ariaLabel, testId }: CopyableCodeProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  if (!code) {
    return (
      <Typography color="text.disabled" sx={{ fontSize: "0.875rem" }}>
        —
      </Typography>
    );
  }

  async function handleCopy(e: React.MouseEvent<HTMLButtonElement>) {
    // Suppress the surrounding row click (which navigates to user detail).
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code as string);
      setCopied(true);
    } catch {
      // Older browsers without the Clipboard API: do nothing. Worst case,
      // organizer can long-press to copy from the visible text.
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        component="span"
        sx={{
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
          fontSize: "0.875rem",
          letterSpacing: "0.04em",
        }}
      >
        {code}
      </Typography>
      <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label={ariaLabel ?? `Copy code ${code}`}
          data-testid={testId}
          sx={{ p: 0.25 }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
